import React, { useState, useEffect } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useAuth } from '@/hooks/useAuth'
import { UserRole } from '@/types'
import { useAppDispatch } from '@store/index'
import { login } from '@store/slices/authSlice'
import HippaConsentDialog from './HippaConsentDialog'
import { ConsentService } from '@/services/consentService'

interface HippaConsentGuardProps {
  children: React.ReactNode
}

/**
 * Guard component that ensures providers have signed HIPAA consent
 * before accessing protected routes
 */
const HippaConsentGuard: React.FC<HippaConsentGuardProps> = ({ children }) => {
  const { user, token, refreshToken, role } = useAuth()
  const dispatch = useAppDispatch()
  const [showConsentDialog, setShowConsentDialog] = useState(false)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    const checkConsentStatus = async () => {
      // Only check for therapist role
      if (role !== UserRole.THERAPIST || !user) {
        setIsChecking(false)
        return
      }

      // Check if user has signed consent
      // If hasHippaSigned is explicitly false or undefined, show dialog
      if (user.hasHippaSigned === false || user.hasHippaSigned === undefined) {
        setShowConsentDialog(true)
      }

      setIsChecking(false)
    }

    checkConsentStatus()
  }, [user, role])

  const handleConsentSubmitted = async () => {
    if (!user || !token) return

    // Update user object with hasHippaSigned: true
    const updatedUser = {
      ...user,
      hasHippaSigned: true,
    }

    // Update Redux store
    dispatch(
      login({
        user: updatedUser,
        token,
        refreshToken: refreshToken || '',
      })
    )

    setShowConsentDialog(false)
  }

  // Show loading while checking consent status
  if (isChecking) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Verifying consent status...
        </Typography>
      </Box>
    )
  }

  // Show consent dialog if needed
  if (showConsentDialog && user) {
    return (
      <>
        <HippaConsentDialog
          open={showConsentDialog}
          user={user}
          onConsentSubmitted={handleConsentSubmitted}
        />
        {/* Block content while dialog is open */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1300, // Below dialog but above content
            pointerEvents: 'none',
          }}
        />
      </>
    )
  }

  // Render children if consent is signed
  return <>{children}</>
}

export default HippaConsentGuard

